// src/api/monthlyReportsApi.js
// Monthly Reports feature — upload source PDFs, generate the client pack, share
// link, PDF export, and archive browse. All calls go through apiClient so the
// MSAL Bearer token is attached automatically.
import apiClient from "./apiClient";

const BASE = "/api/monthly-reports";

// Upload one source PDF. period/companyId are passed as overrides so the whole
// batch lands in one client+period (the type is auto-classified server-side).
export async function uploadReport(file, { period, companyId, type, confirm } = {}) {
  const fd = new FormData();
  fd.append("file", file);
  if (period) fd.append("period", period);
  if (companyId) fd.append("companyId", companyId);
  if (type) fd.append("type", type);
  if (confirm) fd.append("confirm", "true");
  // Content-Type undefined → axios lets the browser set multipart boundary.
  const res = await apiClient.post(`${BASE}/upload`, fd, {
    headers: { "Content-Type": undefined },
    timeout: 120000,
  });
  return res.data;
}

// Assemble + render + persist the pack; returns { shareUrl, sources, ... }.
export async function generateReport(companyId, period) {
  const res = await apiClient.post(`${BASE}/generate`, { companyId, period }, { timeout: 60000 });
  return res.data;
}

// ── Credential hygiene ──────────────────────────────────────────────────────
// buildCredentialHygiene() computes; getCredentialDetail() returns the NAMED
// table for the authenticated in-app view only. The client's shared report
// carries the aggregate, which the backend strips names from at source.
export async function buildCredentialHygiene(companyId, period) {
  const res = await apiClient.post(`${BASE}/credential-hygiene/${companyId}/${period}/build`, {}, { timeout: 300000 });
  return res.data;
}
export async function getCredentialDetail(companyId, period) {
  const res = await apiClient.get(`${BASE}/credential-hygiene/${companyId}/${period}/detail`);
  return res.data?.data || null;
}
// Short-lived, revocable, and NOT the report share token.
export async function createCredentialLink(companyId, period, ttlHours) {
  const res = await apiClient.post(`${BASE}/credential-hygiene/${companyId}/${period}/detail-link`, { ttlHours });
  return res.data;
}
export async function revokeCredentialLink(jti) {
  const res = await apiClient.post(`${BASE}/credential-hygiene/revoke/${jti}`, {});
  return res.data;
}

// IT Glue organisation id per client — lives in the same register mapping as
// tenantId/ownedDomains and is what the credential check uses for passwords.
export async function setItGlueOrgId(companyId, itGlueOrgId) {
  const res = await apiClient.put(`${BASE}/m365/itglue/${companyId}`, { itGlueOrgId });
  return res.data?.data;
}
export async function lookupItGlueOrg(companyId) {
  const res = await apiClient.get(`${BASE}/m365/itglue/lookup/${companyId}`, { timeout: 45000 });
  return res.data;                        // { orgId, companyName, passwords }
}

// ── Inbound reports (mailbox intake) ────────────────────────────────────────
// Vendor PDFs that arrived by email and could not be filed with confidence.
// They are held here rather than guessed at — a wrong guess puts one client's
// data in another client's report.
export async function getIntakeStatus() {
  const res = await apiClient.get(`${BASE}/intake/status`, { timeout: 45000 });
  return res.data;
}
export async function getIntakeQueue() {
  const res = await apiClient.get(`${BASE}/intake/queue`);
  return res.data?.items || [];
}
export async function runIntake({ dryRun = false } = {}) {
  const res = await apiClient.post(`${BASE}/intake/run`, { dryRun }, { timeout: 180000 });
  return res.data;
}
// Confirm files through the SAME store+extract path as a manual upload, and the
// safety guards run again against the client chosen here.
export async function fileIntakeItem(hash, { companyId, type, period }) {
  const res = await apiClient.post(`${BASE}/intake/queue/${hash}/file`, { companyId, type, period }, { timeout: 120000 });
  return res.data;
}
export async function dismissIntakeItem(hash) {
  const res = await apiClient.delete(`${BASE}/intake/queue/${hash}`);
  return res.data;
}
export async function getIntakePdfUrl(hash) {
  return `${BASE}/intake/queue/${hash}/pdf`;
}

// ── Vendor report identity strings (Client Register) ────────────────────────
// The exact client string as each vendor prints it, which often differs from
// the Autotask name ("Plaza Kalgoorlie" vs "Kalgoorlie Plaza Hotel"). Matched
// exactly, never fuzzily.
export async function setReportIdentity(companyId, { reportIdentityBackup, reportIdentityCyber } = {}) {
  const body = {};
  if (reportIdentityBackup !== undefined) body.reportIdentityBackup = reportIdentityBackup;
  if (reportIdentityCyber !== undefined) body.reportIdentityCyber = reportIdentityCyber;
  const res = await apiClient.put(`${BASE}/report-identity/${companyId}`, body);
  return res.data?.data;
}

// ── Microsoft 365 per-client connection (admin consent via /common) ──────────
// No tenant ID is ever typed: the backend mints a signed state, Microsoft hands
// the tenant back on the callback.
export async function getM365Config() {
  const res = await apiClient.get(`${BASE}/m365/config`);
  return res.data;                        // { data:{cid:{tenantId,ownedDomains[]}}, knownTenants[] }
}
export async function startM365Consent(companyId) {
  const res = await apiClient.get(`${BASE}/m365/consent/start`, { params: { companyId } });
  return res.data;                        // { consentUrl, expiresAt, knownTenants }
}
export async function getM365Domains(companyId) {
  const res = await apiClient.get(`${BASE}/m365/domains/${companyId}`, { timeout: 45000 });
  return res.data;                        // { tenantId, tenantName, ownedDomains, domains[] }
}
export async function saveM365Domains(companyId, ownedDomains) {
  const res = await apiClient.put(`${BASE}/m365/domains/${companyId}`, { ownedDomains });
  return res.data?.data;
}
export async function linkM365Tenant(companyId, tenantId) {
  const res = await apiClient.put(`${BASE}/m365/link/${companyId}`, { tenantId }, { timeout: 45000 });
  return res.data;
}
export async function recheckM365(companyId) {
  const res = await apiClient.post(`${BASE}/m365/recheck/${companyId}`, {}, { timeout: 45000 });
  return res.data;
}

// ── FortiGate register mapping (drives the report's Network & Internet section) ──
export async function getFortigateConfig() {
  const res = await apiClient.get(`${BASE}/fortigate/config`);
  return res.data;                        // { data: {cid:{sn,wan}}, configured }
}
export async function getFortigateDevices() {
  const res = await apiClient.get(`${BASE}/fortigate/devices`, { timeout: 60000 });
  return res.data?.data || [];
}
export async function setFortigateConfig(companyId, { sn, wan }) {
  const res = await apiClient.put(`${BASE}/fortigate/config/${companyId}`, { sn, wan });
  return res.data?.data;
}

// ── Month-end snapshot (freeze a completed month) ──
export async function runSnapshot({ period, companyId, sources, force } = {}) {
  const res = await apiClient.post(`${BASE}/snapshot`, { period, companyId, sources, force }, { timeout: 600000 });
  return res.data;
}

// ── Client industry (drives the Did You Know theme) ─────────────────────────
// Stored server-side, not in the app-state snapshot, so the report generator can
// read it at generate time.
export async function getIndustries() {
  const res = await apiClient.get(`${BASE}/industry`);
  return res.data;                        // { data: {cid: industry}, suggestions: [...] }
}
export async function setIndustry(companyId, industry) {
  const res = await apiClient.put(`${BASE}/industry/${companyId}`, { industry });
  return res.data;
}

// ── "Did You Know?" — ONE per month, shared by every client's report ────────
// draft() only returns text; nothing reaches a report until save() is called.
// Written once against the period; the report assembler resolves shared-first
// and falls back to a per-client snippet only for months approved under the old
// client-by-client scheme.
export async function draftSharedDidYouKnow(period, { avoidTopics } = {}) {
  const res = await apiClient.post(`${BASE}/did-you-know-shared/${period}/draft`,
    { avoidTopics }, { timeout: 120000 });
  return res.data;                        // { text, topic, words }
}
export async function getSharedDidYouKnow(period) {
  const res = await apiClient.get(`${BASE}/did-you-know-shared/${period}`);
  return res.data?.data || null;
}
export async function saveSharedDidYouKnow(period, { text, topic }) {
  const res = await apiClient.put(`${BASE}/did-you-know-shared/${period}`, { text, topic });
  return res.data?.data;
}
export async function clearSharedDidYouKnow(period) {
  const res = await apiClient.delete(`${BASE}/did-you-know-shared/${period}`);
  return res.data;
}

// ── per-client "Did You Know?" — legacy, read/clear only ────────────────────
// Nothing writes these any more. Kept so months approved before the shared
// snippet shipped stay readable.
export async function draftDidYouKnow(companyId, period, { industry, avoidTopics } = {}) {
  const res = await apiClient.post(`${BASE}/did-you-know/${companyId}/${period}/draft`,
    { industry, avoidTopics }, { timeout: 120000 });
  return res.data;                        // { text, topic, words }
}
export async function getDidYouKnow(companyId, period) {
  const res = await apiClient.get(`${BASE}/did-you-know/${companyId}/${period}`);
  return res.data?.data || null;
}
export async function saveDidYouKnow(companyId, period, { text, topic }) {
  const res = await apiClient.put(`${BASE}/did-you-know/${companyId}/${period}`, { text, topic });
  return res.data?.data;
}
export async function clearDidYouKnow(companyId, period) {
  const res = await apiClient.delete(`${BASE}/did-you-know/${companyId}/${period}`);
  return res.data;
}

// Per-client source availability for one month — drives the Generate view's
// client-card badges (Cyber / Backup / IT Health / WiFi).
export async function getPeriodStatus(period) {
  const res = await apiClient.get(`${BASE}/period-status/${period}`);
  return res.data;
}

export async function getArchive() {
  const res = await apiClient.get(`${BASE}/archive`);
  return res.data;
}

export async function getShareLink(companyId, period) {
  const res = await apiClient.get(`${BASE}/${companyId}/${period}/share-link`);
  return res.data;
}

export async function getPdfAvailable() {
  const res = await apiClient.get(`${BASE}/pdf-available`);
  return res.data;
}

// Live HTML render (returned as a string for an <iframe srcDoc>).
export async function fetchPreviewHtml(companyId, period) {
  const res = await apiClient.get(`${BASE}/preview/${companyId}/${period}`, {
    responseType: "text", timeout: 60000,
  });
  return res.data;
}

// Fetch the PDF (auth-gated) as a blob and trigger a browser download.
export async function downloadPdf(companyId, period, filename) {
  const res = await apiClient.get(`${BASE}/${companyId}/${period}/pdf`, {
    responseType: "blob", timeout: 120000,
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `monthly-report-${companyId}-${period}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function getReportData(companyId, period) {
  const res = await apiClient.get(`${BASE}/data/${companyId}/${period}`);
  return res.data;
}

// ── Section notes — vCIO commentary pinned to a report section ──────────────
// The section list comes from the BACKEND registry, never a copy in the UI:
// the renderer and the dropdown must offer the same sections, and a hardcoded
// list drifts the moment a section is added or renamed.
export async function getReportSections() {
  const res = await apiClient.get(`${BASE}/sections`);
  return res.data?.sections || [];
}
export async function getSectionNotes(companyId, period) {
  const res = await apiClient.get(`${BASE}/notes/${companyId}/${period}`);
  return res.data?.notes || [];
}
export async function addSectionNote(companyId, period, { sectionKey, text }) {
  const res = await apiClient.post(`${BASE}/notes/${companyId}/${period}`, { sectionKey, text });
  return res.data?.note;
}
export async function updateSectionNote(companyId, period, id, { sectionKey, text }) {
  const res = await apiClient.put(`${BASE}/notes/${companyId}/${period}/${id}`, { sectionKey, text });
  return res.data?.note;
}
export async function deleteSectionNote(companyId, period, id) {
  const res = await apiClient.delete(`${BASE}/notes/${companyId}/${period}/${id}`);
  return res.data;
}

// ── "Discuss at our next catch-up" — the client's own flags ─────────────────
// Written by the CLIENT through the public share link (no auth, share-token
// scoped — see routes/publicReportDiscuss.js). Everything here is the read /
// acknowledge side, which is Craig's and is authenticated.

// Every client with outstanding flags — drives the in-app badge.
export async function fetchDiscussPending() {
  const res = await apiClient.get(`${BASE}/discuss/pending`);
  return res.data?.clients || [];
}

// One client's most recent unaddressed flags (what the MTC prep shows).
export async function fetchDiscussLatest(companyId) {
  const res = await apiClient.get(`${BASE}/discuss/latest/${companyId}`);
  return res.data;
}

// Clears the unread badge WITHOUT changing the flags — opening is not covering.
export async function markDiscussSeen(companyId, period) {
  const res = await apiClient.post(`${BASE}/discuss/${companyId}/${period}/seen`);
  return res.data;
}

// "✓ covered" — stops them resurfacing on every future prep. Reversible, and
// deliberately not a delete: what the client asked is worth keeping.
export async function markDiscussAddressed(companyId, period, addressed = true) {
  const res = await apiClient.post(`${BASE}/discuss/${companyId}/${period}/addressed`, { addressed });
  return res.data;
}

/* ── Report notices — Craig's free-form message under the exec summary ───────
   Two scopes: "global" (every client) and a companyId (one client). They stack
   on a client that has both. */

export async function getNotices(companyIds = []) {
  const res = await apiClient.get(`${BASE}/notices`, { params: { companyIds: companyIds.join(",") } });
  return res.data;
}

export async function getNotice(scope) {
  const res = await apiClient.get(`${BASE}/notices/${encodeURIComponent(scope)}`);
  return res.data.notice;
}

/* ⚠ `image` IS THREE-STATE and the API mirrors that exactly: pass a File to
   REPLACE the banner, pass removeImage:true to DELETE it, and pass NEITHER to
   keep whatever is stored. Collapsing those to two states means every edit of
   the wording silently deletes the picture. */
export async function saveNotice(scope, { title, body, image, removeImage } = {}) {
  const fd = new FormData();
  fd.append("title", title || "");
  fd.append("body", body || "");
  if (image) fd.append("image", image);
  if (removeImage) fd.append("removeImage", "true");
  // Content-Type undefined → the browser sets the multipart boundary.
  const res = await apiClient.post(`${BASE}/notices/${encodeURIComponent(scope)}`, fd, {
    headers: { "Content-Type": undefined },
    timeout: 60000,
  });
  return res.data.notice;
}

export async function clearNotice(scope) {
  const res = await apiClient.delete(`${BASE}/notices/${encodeURIComponent(scope)}`);
  return res.data;
}

// ── "Email report link to client" ───────────────────────────────────────────
// The email is composed on the server from (client, period, share link); the
// page only chooses To and CC. See reportEmailService in the backend.
export async function getEmailRecipients() {
  const res = await apiClient.get(`${BASE}/email-recipients`);
  return res.data;                        // { data: { [companyId]: email } }
}
export async function setEmailRecipient(companyId, email) {
  const res = await apiClient.put(`${BASE}/email-recipients/${companyId}`, { email });
  return res.data;                        // { ok, email }
}
// `test: true` previews / sends the [TEST]-marked variant: subject prefixed,
// a marker line at the top of the body, and the To is NOT remembered as the
// client's recipient.
export async function getEmailDraft(companyId, period, { test = false } = {}) {
  const res = await apiClient.get(`${BASE}/${companyId}/${period}/email-draft`, { params: test ? { test: 1 } : {} });
  return res.data;                        // { to, from, replyTo, subject, text, shareUrl, monthLabel, clientName, test }
}
export async function sendReportEmail(companyId, period, { to, cc, test = false }) {
  const res = await apiClient.post(`${BASE}/${companyId}/${period}/email`, { to, cc, test: !!test }, { timeout: 60000 });
  return res.data;                        // { ok, to, cc, subject, sentAt, test, remembered }
}
